import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Grid,
    IconButton,
    Chip,
    Divider,
    Card,
    CardContent,
    Collapse,
} from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import HistoryIcon from '@mui/icons-material/History';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SessionSaetze from '../../../../shared/components/saetze/SessionSaetze';

function ErgebnisUebungCard({
    uebung,
    onEdit,
    onDelete,
    ergebnisse,
    onChange,
    onDropSatzAdd,
    onDropSatzChange,
    onDropSatzRemove,
    onToggleHistory,
    onOpenFullHistory,
    showHistory,
    letzteErgebnisse,
    isDragging
}) {
    const [expanded, setExpanded] = useState(false);
    const [expandedHistory, setExpandedHistory] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: uebung.uiId });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    const groupedBySession = letzteErgebnisse && Array.isArray(letzteErgebnisse)
        ? letzteErgebnisse.reduce((acc, result) => {
            const key = result.session_id;
            if (!acc[key]) acc[key] = { session_id: result.session_id, timestamp: result.erstellt_am, saetze: [] };
            acc[key].saetze.push(result);
            return acc;
        }, {})
        : {};

    const saetze = Array.isArray(ergebnisse?.saetze) ? ergebnisse.saetze : [];
    const sortedSessions = Object.values(groupedBySession).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return (
        <Card ref={setNodeRef} style={style} sx={{ mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', touchAction: 'pan-y' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

                {/* ── Header ── */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1} flex={1}>
                        <IconButton {...attributes} {...listeners} size="small" sx={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', flexShrink: 0 }}>
                            <DragIndicatorIcon />
                        </IconButton>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{uebung.name}</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                                <Chip label={uebung.zielmuskel} size="small" color="primary" variant="outlined" />
                                <Chip label={uebung.kategorie}  size="small" color="secondary" variant="outlined" />
                            </Box>
                        </Box>
                    </Box>
                    <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                        {expanded && (
                            <IconButton onClick={onToggleHistory} size="small" color="info"><HistoryIcon /></IconButton>
                        )}
                        {!confirmDelete ? (
                            <IconButton onClick={() => setConfirmDelete(true)} size="small" color="error" aria-label={`Lösche Übung ${uebung.name}`}>
                                <DeleteIcon />
                            </IconButton>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton onClick={() => setConfirmDelete(false)} size="small" color="inherit"><CloseIcon /></IconButton>
                                <IconButton onClick={() => { setConfirmDelete(false); onDelete?.(); }} size="small" color="error"><CheckIcon /></IconButton>
                            </Box>
                        )}
                        <IconButton onClick={onEdit} size="small" color="warning"><EditIcon /></IconButton>
                        <IconButton onClick={() => setExpanded(!expanded)} size="small">
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expanded}>
                    {/* ── Historie ── */}
                    {showHistory && sortedSessions.length > 0 && (
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" fontWeight={600}>Trainings-Historie:</Typography>
                                <Button size="small" onClick={() => onOpenFullHistory?.(uebung, letzteErgebnisse)} sx={{ color: '#667eea', textTransform: 'none' }}>
                                    Ganze Historie anzeigen
                                </Button>
                            </Box>

                            {/* Neueste Session */}
                            {sortedSessions.slice(0, 1).map((session, idx) => {
                                const anzNormal = session.saetze.filter(s => !s.ist_dropsatz).length;
                                return (
                                    <Box key={idx}>
                                        <Typography variant="caption" sx={{ color: '#cbd5e1', fontWeight: 500, display: 'block', mb: 1 }}>
                                            {session.timestamp
                                                ? new Date(session.timestamp).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' })
                                                : 'Datum unbekannt'}
                                            {' • '}{anzNormal} Sätze
                                        </Typography>
                                        <SessionSaetze saetze={session.saetze} dropLabelPrefix="Drop " />
                                    </Box>
                                );
                            })}

                            {/* Ältere Sessions (aufklappbar) */}
                            {sortedSessions.length > 1 && (
                                <Box mt={2}>
                                    <Button size="small" onClick={() => setExpandedHistory(!expandedHistory)} sx={{ color: '#667eea', textTransform: 'none' }}>
                                        {expandedHistory ? 'Weniger anzeigen' : `Weitere ${Math.min(2, sortedSessions.length - 1)} Trainings anzeigen`}
                                    </Button>
                                    <Collapse in={expandedHistory}>
                                        {sortedSessions.slice(1, 3).map((session, idx) => {
                                            const anzNormal = session.saetze.filter(s => !s.ist_dropsatz).length;
                                            return (
                                                <Box key={idx} mt={2}>
                                                    <Divider sx={{ my: 1.5, borderColor: 'rgba(102, 126, 234, 0.2)' }} />
                                                    <Typography variant="caption" sx={{ color: '#cbd5e1', fontWeight: 500, display: 'block', mb: 1 }}>
                                                        {session.timestamp
                                                            ? new Date(session.timestamp).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' })
                                                            : 'Datum unbekannt'}
                                                        {' • '}{anzNormal} Sätze
                                                    </Typography>
                                                    <SessionSaetze saetze={session.saetze} dropLabelPrefix="Drop " />
                                                </Box>
                                            );
                                        })}
                                    </Collapse>
                                </Box>
                            )}
                        </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* ── Eingabe: Sätze + ihre Dropsätze ── */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {saetze.length > 0 ? saetze.map((satz, satzIdx) => (
                            <Box key={satzIdx}>
                                {/* Satz-Label */}
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Satz {satzIdx + 1}
                                </Typography>

                                {/* Satz-Felder */}
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6 }}>
                                        <TextField
                                            label="Gewicht (kg)" type="number"
                                            slotProps={{ htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' } }}
                                            size="small" fullWidth
                                            value={satz.gewicht}
                                            onChange={(e) => onChange(uebung.uiId, satzIdx, 'gewicht', e.target.value)}
                                            inputProps={{ step: '0.5' }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <TextField
                                            label="Wiederholungen" type="number"
                                            slotProps={{ htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' } }}
                                            size="small" fullWidth
                                            value={satz.wiederholungen}
                                            onChange={(e) => onChange(uebung.uiId, satzIdx, 'wiederholungen', e.target.value)}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Dropsätze dieses Satzes */}
                                {(satz.dropsaetze || []).map((ds, dsIdx) => (
                                    <Box key={dsIdx} sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'warning.main' }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                                            <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.8rem' }}>
                                                Dropsatz {dsIdx + 1}
                                            </Typography>
                                            <IconButton size="small" color="error" onClick={() => onDropSatzRemove(uebung.uiId, satzIdx, dsIdx)}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 6 }}>
                                                <TextField
                                                    label="Gewicht (kg)" type="number"
                                                    slotProps={{ htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' } }}
                                                    size="small" fullWidth
                                                    value={ds.gewicht}
                                                    onChange={(e) => onDropSatzChange(uebung.uiId, satzIdx, dsIdx, 'gewicht', e.target.value)}
                                                    inputProps={{ step: '0.5' }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6 }}>
                                                <TextField
                                                    label="Wiederholungen" type="number"
                                                    slotProps={{ htmlInput: { inputMode: 'decimal', pattern: '[0-9.]*' } }}
                                                    size="small" fullWidth
                                                    value={ds.wiederholungen}
                                                    onChange={(e) => onDropSatzChange(uebung.uiId, satzIdx, dsIdx, 'wiederholungen', e.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}

                                {/* + Dropsatz Button direkt unter dem Satz */}
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => onDropSatzAdd(uebung.uiId, satzIdx)}
                                    sx={{ mt: 0.75, color: 'warning.main', textTransform: 'none', fontSize: '0.75rem', p: '2px 8px' }}
                                >
                                    Dropsatz
                                </Button>
                            </Box>
                        )) : (
                            <Typography variant="body2" color="text.secondary">Keine Sätze zum Eintragen verfügbar.</Typography>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
}

export default ErgebnisUebungCard;
